<script lang="ts" setup>
import RoomieTag from '@lahaus-roomie/roomie/src/components/RoomieTag/index.vue'
import RoomieCard from '@lahaus-roomie/roomie/src/components/RoomieCard/index.vue'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'

import type { Props } from './types'
import './_index.scss'

import {
  getAreaLabel,
  getDeliveryTermLabel
} from '@/utils/project'
import { convertNumberToWord } from '@/utils/number'

const props = defineProps<Props>()

const { t, n } = useI18n()

const createChatEvent = (projectName: string) =>
  new CustomEvent('app-chat:send-message', {
    detail: { message: t('chatMessage', { project_name: projectName }) }
  })

const navigateToProject = (url: string, projectName: string) => {
  document.dispatchEvent(createChatEvent(projectName))

  return navigateTo(url, {
    open: { target: '_blank' }
  })
}

const goToProject = () =>
  navigateToProject(props.project.path, props.project.name)

const getOptimizedCover = (url: string, width = 300) =>
  `${url}?width=${width}`

const cover = getOptimizedCover(props.project.cover)
</script>

<script lang="ts">
export default {
  name: 'AppChatRecommendationCardV2'
}
</script>

<template>
  <RoomieCard
    :id="`app-chat-recomendation-card-${project.code}`"
    class="app-chat-recomendation-card"
    :data-lh-id="`${screen}-app-chat-recomendation-card`"
    :src="cover"
    :alt="project.name">
    <template #figure>
      <div class="relative">
        <figure>
          <img :src="cover">
        </figure>

        <RoomieTag
          :id="`app-chat-recomendation-card-tag-${project.code}`"
          class="app-chat-recomendation-card__tag"
          variant="information"
          :label="getDeliveryTermLabel(project.delivered_at, t)" />
      </div>
    </template>

    <template #content>
      <div class="flex flex-col gap-16">
        <p class="body-1-sb text-pine-600 truncate">
          {{ project.name }}
        </p>

        <div class="flex flex-col gap-4 body-2-m text-pine-600">
          <p class="truncate">
            · {{ project.neighborhood_label }}, {{ project.city_label }}
          </p>

          <p>
            · {{ t('from', { price: convertNumberToWord(project.price, 'millions', 1, n)}) }}
          </p>

          <p>
            · {{ getAreaLabel(project.typologies, t) }}
          </p>
        </div>
      </div>

      <RoomieButton
        :id="`app-chat-recomendation-card-see-more-${project.code}`"
        :data-lh-id="`${screen}-app-chat-recomendation-card-see-more`"
        class="mt-16 w-fit"
        size="sm"
        variant="primary"
        @click="goToProject">
        {{ t('seeMore') }}
      </RoomieButton>
    </template>
  </RoomieCard>
</template>

<i18n src="./i18n.json" lang="json" />
